import FormularHeader from "components/common/formular-header";
import { ProgressBar } from "components/common/progress-bar";
import { Component } from "react";
import { UsersManagementHome } from "./usersManagementHome";
import ManualAccountCreation, {
  RenderDiv,
  UserType,
} from "./manual-account-creation";
import ExcelAccountCreation from "./excel-account-creation";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import InputTextfield, { parseSchoolClass } from "components/common/inputItem";
import MultiSelect from "components/common/MultiSelect";
import { getSchools } from "components/common/utils";
import CancelPopPup from "./manual-account-creation/CancelPopup";
import StatusPopUp from "components/common/ServerResponsePopup";
import ExcelUploadFeedback from "./ExcelUploadFeedback";
import UserCreationFeedback from "./UserCreationFeedback";
import {
  CreationResponse,
  createUser,
} from "./manual-account-creation/back-call";
import { createStudentsInSchoolFromSpreadsheet } from "server/StartupAdmins CRUD/Requests";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

function withNavigation<T extends object>(Component: React.ComponentType<T>) {
  return function WrappedComponent(props: Omit<T, "navigate">): JSX.Element {
    const navigate = useNavigate();
    return <Component {...(props as T)} navigate={navigate} />;
  };
}

export interface State {
  step: number;
  maxStep: number;
  showCancelPopPup: boolean;
  showCreationResultPopPup: boolean;
  showUserCreationFeedback: boolean;
  isSubmitting: boolean;
  accountCreationState: AccountCreationState;
  schoolList: { [key: string]: string } | null;
  [key: string]: any;
  excelFile: File | null;
  uploadStatus: number;
  uploadMessage: string;
  downloadUrl?: string;
  // Ajoutez ici les autres champs de votre état
}

interface AccountCreationState {
  accountCreationStep: number;
  maxAccountCreationStep: number;
  creationResponse: CreationResponse;
  // onAccountCreationPrev : void ;
  // onAccountCreationNext : void;
  // handleAccountCreationChange : () => void;
  // handleAccountCreationSaveDraft : () => void;
  [key: string]: any;
  // Ajoutez ici les autres champs de votre état
}

interface UsersManagementMultiStepProps {
  navigate?: (path: string) => void;
}

class UsersManagementMultiStep extends Component<
  UsersManagementMultiStepProps,
  State
> {
  defaultState: State = {
    step: 1,
    maxStep: 3,
    schoolList: null, // <- null au lieu de {}
    showCancelPopPup: false,
    showCreationResultPopPup: false,
    showUserCreationFeedback: false,
    isSubmitting: false,
    excelFile: null,
    uploadStatus: 0,
    uploadMessage: "",
    downloadUrl: undefined,
    accountCreationState: {
      // Initialisation de accountCreationState
      accountCreationStep: 1,
      maxAccountCreationStep: 3,
      creationResponse: { status: 0, message: "" },
      userType: "student",
      StudentInfo: {
        firstname: "",
        lastname: "",
        password: "",
        schoolName: "",
        matricule: "",
        schoolClass: "",
        userRole: "student",
        passwordConfirm: "",
      },
      TeacherInfo: {
        firstname: "",
        lastname: "",
        password: "",
        email: "",
        phoneNumber: "",
        passwordConfirm: "",
        userRole: "teacher",
      },
      AdminSchoolInfo: {
        firstname: "",
        lastname: "",
        password: "",
        schoolName: "",
        email: "",
        phoneNumber: "",
        passwordConfirm: "",
        userRole: "admin_school",
      },
      InspectorInfo: {
        firstname: "",
        lastname: "",
        password: "",
        schoolName: "",
        email: "",
        phoneNumber: "",
        passwordConfirm: "",
        userRole: "inspecteur",
      },
      AdminStartupInfo: {
        firstname: "",
        lastname: "",
        password: "",
        email: "",
        phoneNumber: "",
        passwordConfirm: "",
        userRole: "admin",
      },
      ParentInfo: {
        firstname: "",
        lastname: "",
        password: "",
        email: "",
        phoneNumber: "",
        passwordConfirm: "",
        childrenId: [],
        userRole: "parent",
      },
    },
  };
  constructor(props: {}) {
    super(props);
    const savedState = sessionStorage.getItem("UsersManagementMultiStepState");
    const savedStateLocal = localStorage.getItem(
      "UsersManagementMultiStepState",
    );
    this.state = savedState ? JSON.parse(savedState) : this.defaultState;
  }

  componentDidUpdate() {
    // Sauvegarder l'état dans le localStorage à chaque mise à jour du composant
    sessionStorage.setItem(
      "UsersManagementMultiStepState",
      JSON.stringify(this.state),
    );
  }

  async componentDidMount() {
    // Ne pas charger les écoles au démarrage, seulement quand nécessaire
  }

  // Nouvelle méthode pour charger les écoles seulement quand nécessaire
  async loadSchoolsIfNeeded() {
    try {
      const schoolArray = await getSchools();

      // Transformer le tableau en objet pour InputTextfield
      const schoolList: { [key: string]: string } = {};
      schoolArray.forEach((schoolName) => {
        schoolList[schoolName] = schoolName;
      });

      this.setState({ schoolList });
    } catch (error) {
      this.setState({ schoolList: {} }); // fallback vide
    }
  }

  updateInfoField = (
    key:
      | "StudentInfo"
      | "AdminSchoolInfo"
      | "TeacherInfo"
      | "AdminSchoolInfo"
      | "InspectorInfo"
      | "AdminStartupInfo"
      | "ParentInfo",
    field: string,
    value: string | string[],
  ): void => {
    this.setState((prevState) => ({
      accountCreationState: {
        ...prevState.accountCreationState,
        [key]: {
          ...prevState.accountCreationState[key],
          [field]: value,
        },
      },
    }));
  };

  // Fonction pour revenir à l'étape précédente du formulaire principal
  prevStep = (): void => {
    this.setState((prevState) => ({
      step: Math.max(prevState.step - 1, 1),
    }));
  };

  // Fonction pour passer à l'étape suivante du formulaire principal
  nextStep = (): void => {
    this.setState((prevState) => ({
      step: Math.min(prevState.step + 1, prevState.maxStep),
    }));
  };

  // Fonction pour revenir à l'étape précédente de la création de compte
  prevAccountCreationStep = (): void => {
    this.setState((prevState) => ({
      accountCreationState: {
        ...prevState.accountCreationState,
        accountCreationStep: Math.max(
          prevState.accountCreationState.accountCreationStep - 1,
          1,
        ),
      },
    }));
    if (this.state.accountCreationState.accountCreationStep === 1) {
      this.setState((prevState) => ({
        step: Math.max(prevState.step - 1, 1),
      }));
    }
  };

  // Fonction pour passer à l'étape suivante de la création de compte
  nextAccountCreationStep = async (): Promise<void> => {
    // Empêcher les clics multiples pendant le chargement
    if (this.state.isSubmitting) {
      return;
    }

    // Vérification des mots de passe pour les professeurs
    if (
      this.state.accountCreationState.accountCreationStep === 2 &&
      this.state.accountCreationState.userType === "teacher"
    ) {
      const teacherInfo = this.state.accountCreationState.TeacherInfo;
      if (teacherInfo.password !== teacherInfo.passwordConfirm) {
        alert(
          "Les mots de passe ne correspondent pas. Veuillez les saisir à nouveau.",
        );
        return;
      }
    }

    if (
      this.state.accountCreationState.accountCreationStep ==
      this.state.accountCreationState.maxAccountCreationStep
    ) {
      // Activer le loader
      this.setState({ isSubmitting: true });

      try {
        const result = await createUser(this.state);

        setTimeout(() => {
          this.setState((prevState) => ({
            accountCreationState: {
              ...prevState.accountCreationState,
              creationResponse: result,
            },
            showUserCreationFeedback: true,
            isSubmitting: false, // Désactiver le loader
          }));
        }, 500);
      } catch (error) {
        // En cas d'erreur, désactiver le loader
        this.setState({ isSubmitting: false });
        console.error("Erreur lors de la création:", error);
      }
    } else {
      this.setState((prevState) => ({
        accountCreationState: {
          ...prevState.accountCreationState,
          accountCreationStep: Math.min(
            prevState.accountCreationState.accountCreationStep + 1,
            prevState.accountCreationState.maxAccountCreationStep,
          ),
        },
      }));
    }
  };

  // Fonction pour définir le type d'utilisateur
  setUserType = (userTypeValue: UserType): void => {
    this.setState((prevState) => ({
      accountCreationState: {
        ...prevState.accountCreationState,
        userType: userTypeValue,
      },
    }));

    // Charger les écoles seulement si l'utilisateur sélectionne "student"
    if (userTypeValue === "student") {
      this.loadSchoolsIfNeeded();
    }
  };

  onCancel = () => {
    this.setState((prevState) => ({
      ...prevState,
      showCancelPopPup: !prevState.showCancelPopPup,
    }));
  };

  onCancelConfirm = () => {
    this.setState(this.defaultState);
    sessionStorage.setItem(
      "UsersManagementMultiStepState",
      JSON.stringify(this.state),
    );
    localStorage.setItem(
      "UsersManagementMultiStepState",
      JSON.stringify(this.state),
    );
  };

  // Fonction pour mapper les types d'interface utilisateur vers les types d'API
  mapUserTypeToApiType = (uiType: string): string => {
    switch (uiType) {
      case "admin-startup":
        return "admin";
      case "admin-school":
        return "admin_school";
      case "inspecteur":
        return "inspecteur";
      default:
        return uiType; // student, teacher, parent restent identiques
    }
  };

  // Méthode pour récupérer les données de l'utilisateur en cours de création
  getCurrentUserData = () => {
    const { userType } = this.state.accountCreationState;

    switch (userType) {
      case "student":
        return this.state.accountCreationState.StudentInfo;
      case "teacher":
        return this.state.accountCreationState.TeacherInfo;
      case "admin-school":
        return this.state.accountCreationState.AdminSchoolInfo;
      case "admin-startup":
        return this.state.accountCreationState.AdminStartupInfo;
      case "parent":
        return this.state.accountCreationState.ParentInfo;
      case "inspecteur":
        return this.state.accountCreationState.InspectorInfo;
      default:
        return null;
    }
  };

  // Gérer le changement de champs
  handleChange =
    (input: string) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      this.setState({ [input]: e.target.value });
    };

  handleSaveDraft = () => {
    localStorage.setItem(
      "UsersManagementMultiStepState",
      JSON.stringify(this.state),
    );
    this.setState((prevState) => ({
      step: Math.max(prevState.step - 1, 1),
    }));
  };

  classList: { [key: string]: string } = {
    "Terminale A1": "terminale_A1",
    "Terminale A2": "terminale_A2",
    "Terminale B": "terminale_B",
    "Terminale C": "terminale_C",
    "Terminale D": "terminale_D",
    "Terminale G1": "terminale_G1",
    "Terminale G2": "terminale_G2",
    "Terminale G3": "terminale_G3",
    "Troisième MC": "troisieme_mc",
    "Troisième ML": "troisieme_ml",
  };

  backToDefault = (): void => {
    this.setState(this.defaultState);
  };

  // Gestion du fichier uploadé
  handleFileUpload = async (file: File, schoolName: string) => {
    if (!file) {
      this.setState({
        showCreationResultPopPup: true,
        uploadStatus: 400,
        uploadMessage: "Aucun fichier sélectionné",
      });
      return;
    }

    const fileData = await file.arrayBuffer();
    const workbook = XLSX.read(fileData, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (!jsonData.length) {
      this.setState({
        showCreationResultPopPup: true,
        uploadStatus: 400,
        uploadMessage: "Fichier vide ou invalide",
      });
      return;
    }

    const headers = (jsonData[0] as unknown[]).map((header) =>
      String(header).toLowerCase().trim(),
    );
    const requiredColumns = [
      "firstname",
      "lastname",
      "password",
      "matricule",
      "schoolclass",
    ];
    const missingColumns = requiredColumns.filter(
      (col) => !headers.includes(col),
    );

    if (missingColumns.length > 0) {
      this.setState({
        showCreationResultPopPup: true,
        uploadStatus: 400,
        uploadMessage: `Fichier Excel manquant les colonnes suivantes : ${missingColumns.join(
          ", ",
        )}`,
      });
      return;
    }

    try {
      const response = await createStudentsInSchoolFromSpreadsheet(
        file,
        schoolName,
      );

      if (response.status === 200 && response.body instanceof Blob) {
        const url = window.URL.createObjectURL(response.body);
        const link = document.createElement("a");
        link.href = url;
        link.download = `resultats_import_${schoolName}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.setState({
          showCreationResultPopPup: true,
          uploadStatus: 200,
          uploadMessage:
            "Import effectué ! Consultez le fichier de rapport pour plus d'informations.",
          downloadUrl: url,
        });
      } else {
        if (response.body instanceof Blob) {
          const errorText = await response.body.text();
          const errorData = JSON.parse(errorText);

          this.setState({
            showCreationResultPopPup: true,
            uploadStatus: response.status,
            uploadMessage:
              errorData.message || "Une erreur est survenue lors de l'import",
          });
        } else {
          this.setState({
            showCreationResultPopPup: true,
            uploadStatus: response.status,
            uploadMessage:
              response.body.message ||
              "Une erreur est survenue lors de l'import",
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'upload:", error);
      this.setState({
        showCreationResultPopPup: true,
        uploadStatus: 500,
        uploadMessage: "Une erreur est survenue lors de l'import",
      });
    }
  };

  render() {
    const { step, showCancelPopPup, schoolList } = this.state;
    const {
      accountCreationStep,
      userType,
      StudentInfo,
      TeacherInfo,
      AdminSchoolInfo,
      InspectorInfo,
      AdminStartupInfo,
      ParentInfo,
      userRole,
      passwordConfirm,
    } = this.state.accountCreationState;
    const values = {
      userType,
      StudentInfo,
      TeacherInfo,
      AdminSchoolInfo,
      InspectorInfo,
      AdminStartupInfo,
      ParentInfo,
      passwordConfirm,
      userRole,
    };

    // Ajout du loader uniquement pour les étudiants quand les écoles ne sont pas encore chargées
    if (
      step === 2 &&
      accountCreationStep === 2 &&
      userType === "student" &&
      schoolList === null
    ) {
      return (
        <div className="flex items-center justify-center h-full w-full px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-blue-800 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm md:text-base">
              Chargement des écoles...
            </p>
          </div>
        </div>
      );
    }

    switch (step) {
      case 1:
        return (
          <UsersManagementHome
            gotoManualCreationForm={() => {
              this.setState({ step: 2 });
            }}
            gotoExcelCreationForm={() => {
              this.setState({ step: 3 });
            }}
            handleChange={() => {}}
            values={undefined}
          />
        );

      case 2:
        switch (accountCreationStep) {
          case 1:
            return (
              <div className="flex flex-col items-center space-y-8 md:space-y-15 h-full w-full bg-white px-4 md:px-0">
                <FormularHeader
                  onCancel={this.onCancel}
                  onSaveDraft={this.handleSaveDraft}
                  onNext={this.nextAccountCreationStep}
                  onPrev={this.prevAccountCreationStep}
                  isLoading={this.state.isSubmitting}
                />
                {/* <h2 className="text-2xl font-semibold font-['Open Sans']">
                            Création de compte utilisateur
                          </h2> */}
                <ProgressBar
                  totalSteps={
                    this.state.accountCreationState.maxAccountCreationStep
                  }
                  step={this.state.accountCreationState.accountCreationStep}
                />
                <h2 className="text-black text-base md:text-lg font-semibold font-['Open Sans'] text-center px-4">
                  QUEL EST LE TYPE D'UTILISATEUR ?
                </h2>
                <ManualAccountCreation
                  onSend={(userType: UserType) => {
                    this.setUserType(userType);
                  }}
                  handleChange={this.handleChange}
                  values={values}
                />
                {showCancelPopPup && (
                  <CancelPopPup
                    isVisible={true}
                    onCancelAvort={this.onCancel}
                    onCancelConfirm={this.onCancelConfirm}
                  />
                )}
              </div>
            );
          case 2:
            return (
              <div className="flex flex-col items-center space-y-8 md:space-y-15 h-full w-full bg-white px-4 md:px-0">
                <FormularHeader
                  onCancel={this.onCancel}
                  onSaveDraft={this.handleSaveDraft}
                  onNext={this.nextAccountCreationStep}
                  onPrev={this.prevAccountCreationStep}
                  isLoading={this.state.isSubmitting}
                />
                <ProgressBar
                  totalSteps={
                    this.state.accountCreationState.maxAccountCreationStep
                  }
                  step={this.state.accountCreationState.accountCreationStep}
                />
                <h2 className="text-black text-base md:text-lg font-semibold font-['Open Sans'] text-center px-4">
                  REMPLISSEZ LES INFORMATIONS SUIVANTES
                </h2>
                {userType === "student" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-20 w-full max-w-4xl">
                      <div className="space-y-10">
                        <InputTextfield
                          title={"NOM"}
                          value={values.StudentInfo.lastname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(lastname: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                          onSend={(lastname: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                        />
                        <InputTextfield
                          isSelectField={true}
                          selectionList={schoolList || {}}
                          title={"ECOLE"}
                          value={values.StudentInfo.schoolName}
                          isEditable={true}
                          allowFreeText={false}
                          placeholderValue="Sélectionnez une école"
                          onChange={(schoolName: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "schoolName",
                              schoolName,
                            );
                          }}
                          onSend={(schoolName: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "schoolName",
                              schoolName,
                            );
                          }}
                        />
                        <InputTextfield
                          isSelectField={false}
                          title={"N° MATRICULE"}
                          value={values.StudentInfo.matricule}
                          isEditable={true}
                          onChange={(matricule: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "matricule",
                              matricule,
                            );
                          }}
                          onSend={(matricule: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "matricule",
                              matricule,
                            );
                          }}
                        />
                      </div>
                      <div className="space-y-10">
                        <InputTextfield
                          isSelectField={false}
                          title={"PRENOM(S)"}
                          value={values.StudentInfo.firstname}
                          isEditable={true}
                          onChange={(firstname: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                          onSend={(firstname: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                        />
                        <InputTextfield
                          isSelectField={true}
                          selectionList={this.classList}
                          title={"CLASSE"}
                          value={values.StudentInfo.schoolClass}
                          isEditable={true}
                          onChange={(schoolClass: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "schoolClass",
                              schoolClass,
                            );
                          }}
                          onSend={(schoolClass: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "schoolClass",
                              schoolClass,
                            );
                          }}
                        />
                        <InputTextfield
                          isSelectField={false}
                          title={"MOT DE PASSE"}
                          withEyeClosed={true}
                          value={values.StudentInfo.password}
                          isEditable={true}
                          isSecretField={true}
                          onChange={(password: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "password",
                              password,
                            );
                          }}
                          onSend={(password: string) => {
                            this.updateInfoField(
                              "StudentInfo",
                              "password",
                              password,
                            );
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {userType === "teacher" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-20 w-full max-w-4xl">
                      <div className="space-y-10">
                        <InputTextfield
                          title={"NOM"}
                          value={values.TeacherInfo.lastname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(lastname: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                          onSend={(lastname: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"EMAIL"}
                          value={values.TeacherInfo.email}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(email: string) => {
                            this.updateInfoField("TeacherInfo", "email", email);
                          }}
                          onSend={(email: string) => {
                            this.updateInfoField("TeacherInfo", "email", email);
                          }}
                        />
                        <InputTextfield
                          title={"MOT DE PASSE"}
                          value={values.TeacherInfo.password}
                          isEditable={true}
                          withEyeClosed={true}
                          isSelectField={false}
                          isSecretField={true}
                          onChange={(password: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "password",
                              password,
                            );
                          }}
                          onSend={(password: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "password",
                              password,
                            );
                          }}
                        />
                      </div>
                      <div className="space-y-10">
                        <InputTextfield
                          title={"PRENOM(S)"}
                          value={values.TeacherInfo.firstname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(firstname: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                          onSend={(firstname: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"NUMERO DE TELEPHONE"}
                          value={values.TeacherInfo.phoneNumber}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(phoneNumber: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                          onSend={(phoneNumber: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"CONFIRMER MOT DE PASSE"}
                          value={values.TeacherInfo.passwordConfirm}
                          isEditable={true}
                          withEyeClosed={true}
                          isSelectField={false}
                          isSecretField={true}
                          onChange={(passwordConfirm: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "passwordConfirm",
                              passwordConfirm,
                            );
                          }}
                          onSend={(passwordConfirm: string) => {
                            this.updateInfoField(
                              "TeacherInfo",
                              "passwordConfirm",
                              passwordConfirm,
                            );
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {userType === "admin-school" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-20 w-full max-w-4xl">
                      <div className="space-y-10">
                        <InputTextfield
                          title={"NOM"}
                          value={values.AdminSchoolInfo.lastname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(lastname: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                          onSend={(lastname: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"EMAIL"}
                          value={values.AdminSchoolInfo.email}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(email: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "email",
                              email,
                            );
                          }}
                          onSend={(email: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "email",
                              email,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"ECOLE"}
                          value={values.AdminSchoolInfo.schoolName}
                          isEditable={true}
                          isSelectField={true}
                          allowFreeText={true}
                          selectionList={schoolList || {}}
                          placeholderValue="Sélectionnez ou saisissez une école"
                          onChange={(schoolName: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "schoolName",
                              schoolName,
                            );
                          }}
                          onSend={(schoolName: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "schoolName",
                              schoolName,
                            );
                          }}
                        />
                      </div>
                      <div className="space-y-10">
                        <InputTextfield
                          title={"PRENOM(S)"}
                          value={values.AdminSchoolInfo.firstname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(firstname: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                          onSend={(firstname: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"NUMERO DE TELEPHONE"}
                          value={values.AdminSchoolInfo.phoneNumber}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(phoneNumber: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                          onSend={(phoneNumber: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"MOT DE PASSE"}
                          value={values.AdminSchoolInfo.password}
                          withEyeClosed={true}
                          isEditable={true}
                          isSelectField={false}
                          isSecretField={true}
                          onChange={(password: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "password",
                              password,
                            );
                          }}
                          onSend={(password: string) => {
                            this.updateInfoField(
                              "AdminSchoolInfo",
                              "password",
                              password,
                            );
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {userType === "admin-startup" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-20 w-full max-w-4xl">
                      <div className="space-y-10">
                        <InputTextfield
                          title={"NOM"}
                          value={values.AdminStartupInfo.lastname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(lastname: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                          onSend={(lastname: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"EMAIL"}
                          value={values.AdminStartupInfo.email}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(email: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "email",
                              email,
                            );
                          }}
                          onSend={(email: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "email",
                              email,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"MOT DE PASSE"}
                          value={values.AdminStartupInfo.password}
                          withEyeClosed={true}
                          isEditable={true}
                          isSelectField={false}
                          isSecretField={true}
                          onChange={(password: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "password",
                              password,
                            );
                          }}
                          onSend={(password: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "password",
                              password,
                            );
                          }}
                        />
                      </div>
                      <div className="space-y-10">
                        <InputTextfield
                          title={"PRENOM(S)"}
                          value={values.AdminStartupInfo.firstname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(firstname: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                          onSend={(firstname: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"NUMERO DE TELEPHONE"}
                          value={values.AdminStartupInfo.phoneNumber}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(phoneNumber: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                          onSend={(phoneNumber: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"CONFIRMER MOT DE PASSE"}
                          value={values.AdminStartupInfo.passwordConfirm}
                          withEyeClosed={true}
                          isEditable={true}
                          isSelectField={false}
                          isSecretField={true}
                          onChange={(passwordConfirm: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "passwordConfirm",
                              passwordConfirm,
                            );
                          }}
                          onSend={(passwordConfirm: string) => {
                            this.updateInfoField(
                              "AdminStartupInfo",
                              "passwordConfirm",
                              passwordConfirm,
                            );
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {userType === "inspector" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-20 w-full max-w-4xl">
                      <div className="space-y-10">
                        <InputTextfield
                          title={"NOM"}
                          value={values.InspectorInfo.lastname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(lastname: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                          onSend={(lastname: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"EMAIL"}
                          value={values.InspectorInfo.email}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(email: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "email",
                              email,
                            );
                          }}
                          onSend={(email: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "email",
                              email,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"ECOLE"}
                          value={values.InspectorInfo.schoolName}
                          isEditable={true}
                          isSelectField={true}
                          allowFreeText={true}
                          selectionList={schoolList || {}}
                          placeholderValue="Sélectionnez ou saisissez une école"
                          onChange={(schoolName: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "schoolName",
                              schoolName,
                            );
                          }}
                          onSend={(schoolName: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "schoolName",
                              schoolName,
                            );
                          }}
                        />
                      </div>
                      <div className="space-y-10">
                        <InputTextfield
                          title={"PRENOM(S)"}
                          value={values.InspectorInfo.firstname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(firstname: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                          onSend={(firstname: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"NUMERO DE TELEPHONE"}
                          value={values.InspectorInfo.phoneNumber}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(phoneNumber: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                          onSend={(phoneNumber: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"MOT DE PASSE"}
                          value={values.InspectorInfo.password}
                          withEyeClosed={true}
                          isEditable={true}
                          isSelectField={false}
                          isSecretField={true}
                          onChange={(password: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "password",
                              password,
                            );
                          }}
                          onSend={(password: string) => {
                            this.updateInfoField(
                              "InspectorInfo",
                              "password",
                              password,
                            );
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {userType === "parent" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-20 w-full max-w-4xl">
                      <div className="space-y-10">
                        <InputTextfield
                          title={"NOM"}
                          value={values.ParentInfo.lastname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(lastname: string) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                          onSend={(lastname: string) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "lastname",
                              lastname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"EMAIL"}
                          value={values.ParentInfo.email}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(email: string) => {
                            this.updateInfoField("ParentInfo", "email", email);
                          }}
                          onSend={(email: string) => {
                            this.updateInfoField("ParentInfo", "email", email);
                          }}
                        />
                        <InputTextfield
                          title={"NUMERO DE TELEPHONE"}
                          value={values.ParentInfo.phoneNumber}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(phoneNumber: string) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                          onSend={(phoneNumber: string) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "phoneNumber",
                              phoneNumber,
                            );
                          }}
                        />
                      </div>
                      <div className="space-y-10">
                        <InputTextfield
                          title={"PRENOM(S)"}
                          value={values.ParentInfo.firstname}
                          isEditable={true}
                          isSelectField={false}
                          onChange={(firstname: string) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                          onSend={(firstname: string) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "firstname",
                              firstname,
                            );
                          }}
                        />
                        <InputTextfield
                          title={"MOT DE PASSE"}
                          withEyeClosed={true}
                          value={values.ParentInfo.password}
                          isEditable={true}
                          isSelectField={false}
                          isSecretField={true}
                          onChange={(password: string) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "password",
                              password,
                            );
                          }}
                          onSend={(password: string) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "password",
                              password,
                            );
                          }}
                        />
                        <MultiSelect
                          title={"ENFANTS"}
                          selectedItems={values.ParentInfo.childrenId || []}
                          onSelectionChange={(childrenId: string[]) => {
                            this.updateInfoField(
                              "ParentInfo",
                              "childrenId",
                              childrenId,
                            );
                          }}
                          placeholder="Sélectionnez les enfants..."
                        />
                      </div>
                    </div>
                  </>
                )}
                {showCancelPopPup && (
                  <CancelPopPup
                    isVisible={true}
                    onCancelAvort={this.onCancel}
                    onCancelConfirm={this.onCancelConfirm}
                  />
                )}
              </div>
            );
          case 3:
            return (
              <div className="flex flex-col items-center space-y-8 md:space-y-15 h-full w-full bg-white px-4 md:px-0">
                <FormularHeader
                  onCancel={this.onCancel}
                  onSaveDraft={this.handleSaveDraft}
                  onNext={this.nextAccountCreationStep}
                  onPrev={this.prevAccountCreationStep}
                  isMaxStep={true}
                  isLoading={this.state.isSubmitting}
                />
                <ProgressBar
                  totalSteps={
                    this.state.accountCreationState.maxAccountCreationStep
                  }
                  step={this.state.accountCreationState.accountCreationStep}
                />
                <h2 className="text-black text-base md:text-lg font-semibold font-['Open Sans'] text-center px-4">
                  CONFIRMEZ-VOUS LES DONNÉES ?
                </h2>
                <div className="flex flex-col items-start space-y-4 md:space-y-7 w-full max-w-4xl px-4 md:px-0">
                  {userType === "student" && (
                    <>
                      <RenderDiv
                        title="nom"
                        value={values.StudentInfo.lastname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="prénom(s)"
                        value={values.StudentInfo.firstname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="N° MATRICULE"
                        value={values.StudentInfo.matricule}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="CLASSE"
                        value={
                          `${parseSchoolClass(
                            values.StudentInfo.schoolClass,
                          ).level.toUpperCase()}` +
                          " " +
                          `${parseSchoolClass(
                            values.StudentInfo.schoolClass,
                          ).serie.toUpperCase()}`
                        }
                        isHidden={false}
                      />
                      <RenderDiv
                        title="MOT DE PASSE"
                        value={values.StudentInfo.password}
                        isHidden={true}
                      />
                      <RenderDiv
                        title="ECOLE"
                        value={values.StudentInfo.schoolName}
                        isHidden={false}
                      />
                    </>
                  )}
                  {userType === "teacher" && (
                    <>
                      <RenderDiv
                        title="nom"
                        value={values.TeacherInfo.lastname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="prénom(s)"
                        value={values.TeacherInfo.firstname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="email"
                        value={values.TeacherInfo.email}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="numéro de téléphone"
                        value={values.TeacherInfo.phoneNumber}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="mot de passe"
                        value={values.TeacherInfo.password}
                        isHidden={true}
                      />
                    </>
                  )}
                  {userType === "parent" && (
                    <>
                      <RenderDiv
                        title="nom"
                        value={values.ParentInfo.lastname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="prénom(s)"
                        value={values.ParentInfo.firstname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="email"
                        value={values.ParentInfo.email}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="numéro de téléphone"
                        value={values.ParentInfo.phoneNumber}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="mot de passe"
                        value={values.ParentInfo.password}
                        isHidden={true}
                      />
                      <RenderDiv
                        title="enfants sélectionnés"
                        value={`${
                          (values.ParentInfo.childrenId || []).length
                        } enfant(s)`}
                        isHidden={false}
                      />
                    </>
                  )}
                  {userType === "admin-startup" && (
                    <>
                      <RenderDiv
                        title="nom"
                        value={values.AdminStartupInfo.lastname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="prénom(s)"
                        value={values.AdminStartupInfo.firstname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="email"
                        value={values.AdminStartupInfo.email}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="numéro de téléphone"
                        value={values.AdminStartupInfo.phoneNumber}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="mot de passe"
                        value={values.AdminStartupInfo.password}
                        isHidden={true}
                      />
                    </>
                  )}
                  {userType === "admin-school" && (
                    <>
                      <RenderDiv
                        title="nom"
                        value={values.AdminSchoolInfo.lastname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="prénom(s)"
                        value={values.AdminSchoolInfo.firstname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="email"
                        value={values.AdminSchoolInfo.email}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="numéro de téléphone"
                        value={values.AdminSchoolInfo.phoneNumber}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="école"
                        value={values.AdminSchoolInfo.schoolName}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="mot de passe"
                        value={values.AdminSchoolInfo.password}
                        isHidden={true}
                      />
                    </>
                  )}
                  {userType === "inspector" && (
                    <>
                      <RenderDiv
                        title="nom"
                        value={values.InspectorInfo.lastname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="prénom(s)"
                        value={values.InspectorInfo.firstname}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="email"
                        value={values.InspectorInfo.email}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="numéro de téléphone"
                        value={values.InspectorInfo.phoneNumber}
                        isHidden={false}
                      />
                      <RenderDiv
                        title="mot de passe"
                        value={values.InspectorInfo.password}
                        isHidden={true}
                      />
                    </>
                  )}
                  <button
                    onClick={this.prevAccountCreationStep}
                    className="flex flex-row bg-[#E3B505] text-[#131CC9] rounded rounded-lg justify-center items-center self-end gap-2 md:gap-2.5 inline-flex px-3 md:px-4 py-2"
                  >
                    <PencilSquareIcon
                      className="w-5 h-5 md:w-6 md:h-6 ml-2 md:ml-3 self-center"
                      color="#131CC9"
                      aria-hidden={"false"}
                    />
                    <div className="flex mr-1 md:mr-2 mt-1 md:mt-2 mb-1 md:mb-2 text-[#131CC9] text-sm md:text-base font-semibold font-['Open Sans'] self-center">
                      ÉDITER
                    </div>
                  </button>
                  {showCancelPopPup && (
                    <CancelPopPup
                      isVisible={true}
                      onCancelAvort={this.onCancel}
                      onCancelConfirm={this.onCancelConfirm}
                    />
                  )}
                  {/* Nouveau feedback pour la création d'utilisateur */}
                  <UserCreationFeedback
                    open={this.state.showUserCreationFeedback}
                    status={
                      this.state.accountCreationState.creationResponse.status
                    }
                    message={
                      this.state.accountCreationState.creationResponse.message
                    }
                    userType={this.state.accountCreationState.userType}
                    userData={this.getCurrentUserData()}
                    onClose={() => {
                      this.setState({
                        showUserCreationFeedback: false,
                        isSubmitting: false, // Désactiver le loader
                      });
                      if (
                        this.state.accountCreationState.creationResponse
                          .status === 200
                      ) {
                        this.setState(this.defaultState, () => {
                          if (this.props.navigate) {
                            this.props.navigate("/users-management");
                          }
                        });
                      } else {
                        setTimeout(() => {
                          this.setState((prevState) => ({
                            ...prevState,
                            accountCreationState: {
                              ...prevState.accountCreationState,
                              accountCreationStep: Math.max(
                                prevState.accountCreationState
                                  .accountCreationStep - 1,
                                1,
                              ),
                            },
                          }));
                        }, 500);
                      }
                    }}
                    onRetry={() => {
                      this.setState({
                        showUserCreationFeedback: false,
                        isSubmitting: false, // Désactiver le loader
                      });
                      // Retourner à l'étape précédente pour permettre la modification
                      this.setState((prevState) => ({
                        ...prevState,
                        accountCreationState: {
                          ...prevState.accountCreationState,
                          accountCreationStep: Math.max(
                            prevState.accountCreationState.accountCreationStep -
                              1,
                            1,
                          ),
                        },
                      }));
                    }}
                  />
                </div>
              </div>
            );
          default:
            return null;
        }

      case 3:
        return (
          <div className="flex flex-col items-center space-y-15 h-full w-full bg-white">
            <ExcelAccountCreation
              backToDefault={this.backToDefault}
              handleFileUpload={this.handleFileUpload}
              uploadStatus={this.state.uploadStatus}
            />
            {this.state.showCreationResultPopPup && (
              <ExcelUploadFeedback
                open={this.state.showCreationResultPopPup}
                status={this.state.uploadStatus}
                message={this.state.uploadMessage}
                downloadUrl={
                  this.state.uploadStatus === 200
                    ? this.state.downloadUrl
                    : undefined
                }
                onClose={() =>
                  this.setState({
                    showCreationResultPopPup: false,
                  })
                }
              />
            )}
          </div>
        );

      default:
        return null;
    }
  }
}

export default withNavigation(UsersManagementMultiStep);
